import { useEffect, useRef, useState } from 'react';
import { navigateToRoom } from '../lib/code';
import { gameIconSlug, gameLabel } from '../lib/game-meta';
import { type PublicRoom, roomClockLabel, roomModeLabel } from '../lib/public-rooms';
import { fetchWithTurnstileRetry } from '../lib/turnstile';
import type { GameId } from '../wasm';
import type { MatchConfig } from '../proto';
import { Icon } from './Icon';
import { MODE_PRESETS, ModeIcon, modeIdForConfig } from './ModePicker';
import './home-lobby.css';

function Seat({ empty = false }: { empty?: boolean }) {
  return (
    <svg className={`lobby-seat${empty ? ' is-empty' : ''}`} viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      {empty ? <><circle cx="12" cy="12" r="8" strokeDasharray="2 3" /><path d="M8.5 12h7M12 8.5v7" /></> : <><circle cx="12" cy="7" r="3" /><path d="M5.5 20v-3a6.5 6.5 0 0 1 13 0v3z" /></>}
    </svg>
  );
}

function GameIcons({ config }: { config: MatchConfig }) {
  const mode = MODE_PRESETS.find((preset) => preset.id === modeIdForConfig(config))!;
  return (
    <span className="lobby-games" aria-hidden="true">
      {config.games.length > 1 && <span className="lobby-mode" data-mode={mode.id} title={`${mode.label}：${mode.hint}`}><ModeIcon id={mode.id} /></span>}
      {config.games.map((game, index) => (
        <img key={`${index}-${game}`} src={`${import.meta.env.BASE_URL}icons/games/${gameIconSlug(game as GameId)}.svg`} width="28" height="28" alt="" title={gameLabel(game as GameId)} />
      ))}
    </span>
  );
}

/** Fetch once on entry. Empty lobbies take no space in the home menu. */
export function HomeLobby({ previewRooms }: { previewRooms?: PublicRoom[] }) {
  const [rooms, setRooms] = useState<PublicRoom[]>(previewRooms?.slice(0, 5) ?? []);
  const [selected, setSelected] = useState<PublicRoom | null>(null);
  const [entering, setEntering] = useState(false);
  const [entryError, setEntryError] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (previewRooms !== undefined) {
      setRooms(previewRooms.slice(0, 5));
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8_000);
    void fetch('/api/rooms', { signal: controller.signal, credentials: 'omit', cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) return;
        const body = await response.json() as { rooms: PublicRoom[] };
        if (!controller.signal.aborted && Array.isArray(body.rooms)) setRooms(body.rooms.slice(0, 5));
      })
      .catch(() => { /* Keep the menu quiet when discovery is unavailable. */ })
      .finally(() => clearTimeout(timeout));
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [previewRooms]);

  useEffect(() => {
    const element = dialog.current;
    if (selected && element && !element.open) element.showModal();
    if (!selected && element?.open) element.close();
  }, [selected]);

  function closeDetails() {
    if (!entering) {
      setSelected(null);
      setEntryError(null);
    }
  }

  async function enterRoom() {
    if (!selected || entering) return;
    if (previewRooms !== undefined) {
      setEntryError('表示サンプルです。実際の対局には接続しません。');
      return;
    }
    setEntering(true);
    setEntryError(null);
    try {
      const response = await fetchWithTurnstileRetry(`/api/rooms/${selected.code}`, { credentials: 'include', cache: 'no-store' });
      if (response.status === 404 || response.status === 410) {
        setRooms((current) => current.filter((room) => room.code !== selected.code));
        setEntryError('この部屋は閉じられました。');
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const current = await response.json() as { state: string; guest_sub: string | null };
      if (current.state === 'finished') {
        setRooms((list) => list.filter((room) => room.code !== selected.code));
        setEntryError('この対局は終了しました。');
        return;
      }
      // Room/MatchDO resolves the current seat atomically. A newly full room opens as a spectator.
      navigateToRoom(selected.code);
    } catch {
      setEntryError('部屋を確認できませんでした。もう一度お試しください。');
    } finally {
      setEntering(false);
    }
  }

  if (rooms.length === 0 && !selected) return null;
  return (
    <section className="home-lobby" aria-label="公開対局" data-testid="home-lobby">
      <div className="lobby-grid">
        {rooms.map((room) => {
          const games = room.config.games.map((game) => gameLabel(game as GameId)).join('・');
          const clock = roomClockLabel(room.config.clock);
          const mode = room.config.games.length > 1 ? `${roomModeLabel(room.config.orchestrator)}、` : '';
          const action = room.state === 'playing' ? '観戦' : room.guest_name ? '部屋を見る' : '参加';
          return (
            <button key={room.code} type="button" className="lobby-room" aria-label={`${room.host_name}${room.guest_name ? ` 対 ${room.guest_name}` : '・空席あり'}、${mode}${games}、${clock}、${action}の詳細`} data-testid={`lobby-room-${room.code}`} onClick={() => { setSelected(room); setEntryError(null); }}>
              <span className="lobby-player-name lobby-host" title={room.host_name}>{room.host_name}</span>
              <GameIcons config={room.config} />
              <span className={`lobby-player-name lobby-guest${room.guest_name === null ? ' is-open' : ''}`} title={room.guest_name ?? '参加'}>{room.guest_name ?? '参加'}</span>
              <span className="lobby-room-clock">{clock}</span>
            </button>
          );
        })}
      </div>
      <dialog ref={dialog} className="lobby-dialog" aria-labelledby="lobby-detail-title" onCancel={(event) => { event.preventDefault(); closeDetails(); }} onClose={() => { setSelected(null); setEntryError(null); }} onClick={(event) => { if (event.target === event.currentTarget) { const bounds = event.currentTarget.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDetails(); } }}>
        {selected && <>
          <div className="lobby-detail-top"><h2 id="lobby-detail-title">{selected.host_name} の部屋</h2><button type="button" className="lobby-detail-close" aria-label="閉じる" onClick={closeDetails} disabled={entering}><Icon name="close" /></button></div>
          <div className="lobby-detail-games">{selected.config.games.map((game, index) => <span key={`${index}-${game}`}><img src={`${import.meta.env.BASE_URL}icons/games/${gameIconSlug(game as GameId)}.svg`} width="28" height="28" alt="" />{gameLabel(game as GameId)}</span>)}</div>
          <div className="lobby-detail-players"><span><Seat />{selected.host_name}</span>{selected.guest_name && <><span className="lobby-player-divider">/</span><span><Seat />{selected.guest_name}</span></>}</div>
          <p className="lobby-detail-clock">{roomClockLabel(selected.config.clock)}<span>·</span>{roomModeLabel(selected.config.orchestrator)}</p>
          {entryError && <p className="lobby-entry-error" role="status">{entryError}</p>}
          <button type="button" className="btn-primary lobby-enter" disabled={entering} onClick={() => void enterRoom()}>{entering ? '確認中…' : selected.state === 'playing' ? '観戦する' : selected.guest_name ? '部屋を見る' : '参加する'}<Icon name="next" size={18} /></button>
        </>}
      </dialog>
    </section>
  );
}
